import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Instagram, Linkedin } from "lucide-react";
import { Reveal, MaskedLine } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIALS = [
  {
    key: "instagram-pursuit",
    icon: Instagram,
    label: "Instagram",
    handle: "@pursuit_media_studios",
    url: "https://www.instagram.com/pursuit_media_studios/",
  },
  {
    key: "instagram-colin",
    icon: Instagram,
    label: "Instagram",
    handle: "@colin__wuu",
    url: "https://www.instagram.com/colin__wuu/",
  },
  {
    key: "linkedin",
    icon: Linkedin,
    label: "LinkedIn",
    handle: "Colin Wu",
    url: "https://www.linkedin.com/",
  },
];

const PROJECT_TYPES = [
  "Event Coverage",
  "Commercial Film",
  "Photography",
  "Post & Colour",
  "Driver / Team Documentary",
  "Other",
];

const inputCls =
  "w-full bg-transparent border border-white/15 text-white text-sm font-light px-4 py-3.5 placeholder:text-white/35 focus:outline-none focus:border-signal transition-colors duration-300";

export default function Contact({ intent }) {
  const [form, setForm] = useState({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (intent) {
      setForm((f) => ({ ...f, project_type: intent }));
    }
  }, [intent]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent — we'll be in touch shortly.", {
        description: "Thanks for reaching out to Pursuit Media Studios.",
      });
      setForm({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-surface py-24 md:py-36">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-14 md:gap-24">
        <div>
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.4em] text-signal mb-4">06 — CONTACT</p>
          </Reveal>
          <h2
            className="font-display text-5xl md:text-7xl text-white leading-[0.92] mb-8"
            style={{ fontWeight: 900 }}
          >
            <MaskedLine>LET'S MAKE</MaskedLine>
            <MaskedLine delay={0.1}>YOUR NEXT</MaskedLine>
            <MaskedLine delay={0.2}>
              <span className="text-outline-signal">PROJECT MOVE</span>
            </MaskedLine>
          </h2>
          <Reveal delay={0.15}>
            <p className="text-white/70 text-base font-light leading-relaxed mb-12 max-w-md">
              Bookings, collaborations and event coverage enquiries — send it through the
              form or reach us on socials.
            </p>
          </Reveal>

          <div className="space-y-1">
            {SOCIALS.map(({ icon: Icon, label, handle, url, key }, i) => (
              <Reveal key={key} delay={0.05 * i}>
                <a
                  data-testid={`social-link-${key}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between border-t border-white/10 last:border-b py-4 hover:pl-2 transition-all duration-300"
                >
                  <span className="flex items-center gap-4">
                    <Icon size={18} className="text-signal" />
                    <span className="font-mono text-[11px] tracking-[0.25em] text-white">{label}</span>
                  </span>
                  <span className="flex items-center gap-2 font-mono text-[11px] text-smoke">
                    {handle}
                    <ArrowUpRight
                      size={14}
                      className="text-white/40 group-hover:text-signal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300"
                    />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <form
            data-testid="contact-inquiry-form"
            onSubmit={submit}
            className="border border-white/10 bg-ink/60 backdrop-blur-sm p-6 md:p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="c-name" className="font-mono text-[10px] tracking-[0.3em] text-smoke block mb-2">
                  NAME *
                </label>
                <input
                  id="c-name"
                  data-testid="contact-input-name"
                  className={inputCls}
                  placeholder="Your name"
                  value={form.name}
                  onChange={update("name")}
                  required
                />
              </div>
              <div>
                <label htmlFor="c-email" className="font-mono text-[10px] tracking-[0.3em] text-smoke block mb-2">
                  EMAIL *
                </label>
                <input
                  id="c-email"
                  data-testid="contact-input-email"
                  type="email"
                  className={inputCls}
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={update("email")}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="c-type" className="font-mono text-[10px] tracking-[0.3em] text-smoke block mb-2">
                PROJECT TYPE
              </label>
              <select
                id="c-type"
                data-testid="contact-input-project-type"
                className={inputCls}
                value={form.project_type}
                onChange={update("project_type")}
              >
                {PROJECT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="c-msg" className="font-mono text-[10px] tracking-[0.3em] text-smoke block mb-2">
                THE BRIEF *
              </label>
              <textarea
                id="c-msg"
                data-testid="contact-input-message"
                rows={5}
                className={`${inputCls} resize-none`}
                placeholder="Tell us about the event, car or story you want captured..."
                value={form.message}
                onChange={update("message")}
                required
              />
            </div>

            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="w-full bg-signal text-white font-mono text-[11px] tracking-[0.3em] py-4 hover:bg-white hover:text-ink transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? "SENDING..." : "SEND ENQUIRY"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
